import { useCallback, useEffect, useMemo, useState } from 'react';
import { getInterestRates, submitLoan } from '../services/externalLoanService';
import { calculateEligibleAmount, validateExternalLoanForm } from '../utils/externalLoanValidators';
import {
  EXTERNAL_LOAN_DEFAULT_DAILY_INTEREST_RATE_PERCENT,
  EXTERNAL_LOAN_PROTECTED_SAVINGS_PERCENTAGE,
  EXTERNAL_LOAN_REPAYMENT_FREQUENCY_DAILY,
  EXTERNAL_LOAN_STANDARD_INTEREST_PLAN,
} from '../constants/externalLoanConstants';

export const initialExternalLoanFormState = {
  amount: '',
  dailyInterestRatePercent: EXTERNAL_LOAN_DEFAULT_DAILY_INTEREST_RATE_PERCENT,
  repaymentTerm: '',
  purpose: '',
  repaymentFrequency: EXTERNAL_LOAN_REPAYMENT_FREQUENCY_DAILY,
  interestPlan: EXTERNAL_LOAN_STANDARD_INTEREST_PLAN,
  notes: '',
};

export const buildExternalLoanSubmissionPayload = (source = {}) => {
  const formState = source.loanDetails || source;
  const declarations = source.declarations || {};

  const payload = {
    amount: Number(formState.amount),
    repayment_term: Number(formState.repaymentTerm),
    purpose: formState.purpose,
    repayment_frequency: EXTERNAL_LOAN_REPAYMENT_FREQUENCY_DAILY,
    interest_plan: EXTERNAL_LOAN_STANDARD_INTEREST_PLAN,
    daily_interest_rate: Number(formState.dailyInterestRatePercent),
    notes: formState.notes,
    information_confirmed: Boolean(declarations.informationConfirmed),
    approval_flow_acknowledged: Boolean(declarations.approvalFlowAcknowledged),
  };

  if (formState.interestRateId) {
    payload.interest_rate_id = Number(formState.interestRateId);
  }

  return payload;
};

export const useExternalLoan = (memberSummary = {}) => {
  const [uiState, setUiState] = useState({ loading: true, submitting: false, error: null, success: null });
  const [formState, setFormState] = useState(initialExternalLoanFormState);
  const [apiState, setApiState] = useState({ interestRates: [], applications: [] });
  const [formErrors, setFormErrors] = useState({});

  const currentSavings = Number(memberSummary.currentSavings || 0);
  const protectedSavingsPercentage = Number(memberSummary.protectedSavingsPercentage || EXTERNAL_LOAN_PROTECTED_SAVINGS_PERCENTAGE);
  const eligibleAmount = useMemo(
    () => calculateEligibleAmount(currentSavings, protectedSavingsPercentage),
    [currentSavings, protectedSavingsPercentage],
  );

  const loadData = useCallback(async () => {
    try {
      setUiState((prev) => ({ ...prev, loading: true, error: null }));
      const rates = await getInterestRates().catch(() => []);
      setApiState((prev) => ({
        ...prev,
        interestRates: Array.isArray(rates) ? rates : [],
      }));
    } catch (error) {
      setUiState((prev) => ({ ...prev, error: error.message || 'Unable to load external loan data.' }));
    } finally {
      setUiState((prev) => ({ ...prev, loading: false }));
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const updateForm = useCallback((field, value) => {
    setFormState((prev) => ({
      ...prev,
      [field]: field === 'interestPlan'
        ? EXTERNAL_LOAN_STANDARD_INTEREST_PLAN
        : field === 'repaymentFrequency'
          ? EXTERNAL_LOAN_REPAYMENT_FREQUENCY_DAILY
          : value,
    }));
    setFormErrors((prev) => ({ ...prev, [field]: undefined }));
  }, []);

  const submit = useCallback(async () => {
    const validation = validateExternalLoanForm({
      amount: formState.amount,
      dailyInterestRatePercent: formState.dailyInterestRatePercent,
      repaymentTerm: formState.repaymentTerm,
      purpose: formState.purpose,
      eligibleAmount,
    });

    if (!validation.isValid) {
      setFormErrors(validation.errors);
      return { ok: false, errors: validation.errors };
    }

    try {
      setUiState((prev) => ({ ...prev, submitting: true, error: null, success: null }));
      const response = await submitLoan(buildExternalLoanSubmissionPayload(formState));
      setApiState((prev) => ({ ...prev, applications: [response, ...prev.applications] }));
      setUiState((prev) => ({ ...prev, success: 'Your external loan application has been submitted successfully.' }));
      setFormState(initialExternalLoanFormState);
      return { ok: true, response };
    } catch (error) {
      const message = error?.message || 'Unable to submit the application.';
      setUiState((prev) => ({ ...prev, error: message }));
      return { ok: false, error: message };
    } finally {
      setUiState((prev) => ({ ...prev, submitting: false }));
    }
  }, [eligibleAmount, formState]);

  return {
    uiState,
    formState,
    apiState,
    formErrors,
    eligibleAmount,
    currentSavings,
    protectedSavingsPercentage,
    setFormState,
    updateForm,
    submit,
    retry: loadData,
  };
};
